import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./About.css";
import Testimonial from "../../../Testimonial/Testimonial";

const About = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1;

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      <Helmet>
        <title>About Us | Tools & Technology Stack</title>
        <meta
          name="description"
          content="Discover our expertise in cutting-edge technologies including Angular, React, Node.js, PHP, Python, Laravel, Blockchain, AI, IoT, Cloud, Data Science, Mobile Apps, AR/VR, and more."
        />
        <meta
          name="keywords"
          content="Angular, React, React Native, Node.js, PHP, Laravel, Django, CodeIgniter, Bootstrap, HTML, CSS3, JavaScript, MongoDB, MySQL, PostgreSQL, SQL Server, Oracle, MariaDB, AmazonDB, Salesforce, Appian, Solidity, Ethereum, Hyperledger, Blockchain, ERC-20, Go Chain, One Ledger, Artificial Intelligence, Machine Learning, Data Science, Internet of Things, Virtual Reality, Augmented Reality, Flutter, Ionic, Android, iOS, SaaS, jQuery, Less, Sass, Cloud Computing, Web Development, Mobile App Development, Software Development"
        />
      </Helmet>

      <div className="about-us-container">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content-about">
            <h1 className="hero-title animate-fade-in">
              Innovating the Future of Technology
            </h1>
            <p className="hero-subtitle animate-fade-in-delay">
              MK E-Tech - Where vision meets execution
            </p>
            <a
              href="https://wa.me/919155599968?text=Hi!%20I%20would%20like%20to%20know%20more%20about%20your%20company%20and%20the%20solutions%20you%20offer.%20Please%20share%20details%20about%20your%20services%20and%20approach."
              target="_blank"
              rel="noreferrer"
            >
              <button className="cta-button animate-bounce">
                Explore Our Solutions
              </button>
            </a>
          </div>
          <div className="hero-visual">
            <div className="floating-shape shape-1"></div>
            <div className="floating-shape shape-2"></div>
            <div className="floating-shape shape-3"></div>
          </div>
        </section>

        {/* Company Story */}
        <section className="story-section">
          <div className="container">
            <div className="section-title">
              <h2>Our Story</h2>
              <div className="title-underline"></div>
            </div>
            <div className="story-content">
              <div className="story-text">
                <p>
                  Founded in 2014, MK E-Tech has been at the forefront of
                  digital innovation, providing cutting-edge software solutions
                  to businesses worldwide. What started as a small team of
                  passionate developers has now grown into a multinational
                  technology powerhouse.
                </p>
                <p>
                  Our journey has been marked by relentless pursuit of
                  excellence, commitment to quality, and unwavering dedication
                  to our clients' success. We believe in the transformative
                  power of technology to solve complex business challenges.
                </p>
              </div>
              <div className="story-visual">
                <div className="timeline-animation">
                  <div className="timeline-point"></div>
                  <div className="timeline-point"></div>
                  <div className="timeline-point"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="mission-section">
          <div className="container">
            <div className="cards-container">
              <div className="card mission-card slide-in-left">
                <div className="card-icon">🚀</div>
                <h3>Our Mission</h3>
                <p>
                  To empower businesses with innovative software solutions that
                  drive growth, efficiency, and competitive advantage in the
                  digital era.
                </p>
              </div>
              <div className="card vision-card slide-in-right">
                <div className="card-icon">🔭</div>
                <h3>Our Vision</h3>
                <p>
                  To be the world's most trusted technology partner,
                  transforming industries through cutting-edge digital
                  solutions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}

        <section className="values-section-about">
          <div className="container">
            <div className="section-title">
              <h2>Our Values</h2>
              <div className="title-underline"></div>
              <p className="section-subtitle">
                The principles that guide everything we do at MK E-Tech
              </p>
            </div>
            <div className="values-container">
              <div className="value-item fade-in">
                <div className="value-icon">💡</div>
                <h4>Innovation</h4>
                <p>
                  We constantly push boundaries to deliver groundbreaking
                  solutions.
                </p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">⭐</div>
                <h4>Excellence</h4>
                <p>
                  We strive for perfection in everything we do, from code to
                  customer service.
                </p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">🤝</div>
                <h4>Integrity</h4>
                <p>We maintain transparency and honesty in all our dealings.</p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">👥</div>
                <h4>Collaboration</h4>
                <p>We believe great things happen when we work together.</p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">⚡</div>
                <h4>Agility</h4>
                <p>
                  We adapt quickly to changing technologies and market demands.
                </p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">❤️</div>
                <h4>Customer Centricity</h4>
                <p>
                  Our clients' success is the ultimate measure of our value.
                </p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">🌱</div>
                <h4>Sustainability</h4>
                <p>
                  We develop solutions that are environmentally and socially
                  responsible.
                </p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">🌎</div>
                <h4>Global Mindset</h4>
                <p>
                  We think globally while respecting local cultures and needs.
                </p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">🚀</div>
                <h4>Continuous Learning</h4>
                <p>
                  We invest in growth and development to stay at technology's
                  cutting edge.
                </p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">🛡️</div>
                <h4>Security First</h4>
                <p>
                  We prioritize the protection of our clients' data and digital
                  assets.
                </p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">🎯</div>
                <h4>Results-Driven</h4>
                <p>
                  We focus on delivering measurable outcomes and tangible
                  business value.
                </p>
              </div>
              <div className="value-item fade-in">
                <div className="value-icon">🤲</div>
                <h4>Inclusivity</h4>
                <p>
                  We embrace diversity and create environments where everyone
                  can thrive.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="stats-section">
          <div className="container">
            <div className="stats-container">
              <div className="stat">
                <h3 className="counter" data-target="500">
                  500
                </h3>
                <p>Projects Completed</p>
              </div>
              <div className="stat">
                <h3 className="counter" data-target="150">
                  150
                </h3>
                <p>Happy Clients</p>
              </div>
              <div className="stat">
                <h3 className="counter" data-target="50">
                  50
                </h3>
                <p>Team Members</p>
              </div>
              <div className="stat">
                <h3 className="counter" data-target="12">
                  11
                </h3>
                <p>Years of Experience</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <h2>Ready to Transform Your Business?</h2>
            <p>Let's discuss how our solutions can drive your success</p>
            <a
              href="https://wa.me/919155599968?text=Hi!%20I%20am%20ready%20to%20transform%20my%20business%20with%20your%20solutions.%20Please%20connect%20me%20with%20your%20team%20to%20discuss%20the%20next%20steps."
              target="_blank"
              rel="noreferrer"
            >
              <button className="cta-button">Get in Touch</button>
            </a>
          </div>
        </section>
      </div>

      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
